import type { GridApi } from 'ag-grid-community'
import { useAppDispatch, useAppSelector } from 'appState/store'
import { updateAllClaimsRoute, updateCommonClaimsFilters } from 'features/claims/claimsSlice'
import { useCallback, useEffect, useRef, useState } from 'react'
import { formatDateOnly } from 'utils/date'
import { notificator } from 'utils/notificator/notificator'
import { normalizeAppealStatusValues } from 'features/claims/claimsApi'
import { sanitizeYMD } from './dateUtils'
import type { FieldIds, FilterFormState, LastModifiedQuickState } from './filterTypes'
import { INITIAL_FILTER_FORM_STATE } from './filterTypes'
import {
  buildBatchContextFilterModel,
  buildFilterModel,
  buildRollingDateFilter,
  extractFormFromFilterModel,
} from './filterUtils'

export const useExternalFilter = (api: GridApi | null | undefined, fields: FieldIds) => {
  const dispatch = useAppDispatch()
  const { commonClaimsFilters } = useAppSelector((s) => s.claims)
  const { batchCode, batchId } = useAppSelector((s) => s.claims.allClaimRouteUrl)

  // “Real” batch-processing context = both code & id present
  const isBatchContext = Boolean(batchCode && batchId)

  const [form, setForm] = useState<FilterFormState>(INITIAL_FILTER_FORM_STATE)
  const [lastModQuick, setLastModQuick] = useState<LastModifiedQuickState>({ model: null, text: '' })
  const [filterLength, setFilterLength] = useState(0)

  // Track if we've already applied the batch-context overrides
  const batchHydratedRef = useRef(false)
  const pendingFilterModelRef = useRef<any | null>(null)

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Badge count based on Redux filters
  useEffect(() => {
    setFilterLength(Object.keys(commonClaimsFilters || {}).length)
  }, [commonClaimsFilters])

  const updateFormField = useCallback(<K extends keyof FilterFormState>(key: K, value: FilterFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }, [])

  const setGridFilterModel = useCallback(
    (model: any) => {
      if (!api || (api as any).isDestroyed?.()) return

      pendingFilterModelRef.current = model
      api.setFilterModel(model)
      window.setTimeout(() => {
        if (pendingFilterModelRef.current === model) {
          pendingFilterModelRef.current = null
        }
      }, 0)
    },
    [api],
  )

  /**
   * Apply button: validate form, build filter model, merge with grid filters,
   * then push into AG Grid.
   *
   * NOTE: Persistence to prefs stays in ClaimsTable.onFilterChanged
   */
  const applyFilters = useCallback(async () => {
    if (!api || (api as any).isDestroyed?.()) return

    // 1) Basic validation for short text fields
    for (const key in form) {
      if (key !== 'batchCode' && typeof form[key as keyof FilterFormState] === 'string') {
        const value = form[key as keyof FilterFormState] as string
        if (value && value.length > 0 && value.length < 3) {
          notificator.notify({
            type: 'warn',
            heading: 'Filter Tip',
            message: 'Please enter at least 3 characters to use this filter',
          })
          return
        }
      }
    }

    if (form.batchCode) {
      const num = parseInt(form.batchCode, 10)
      if (isNaN(num) || num < 1 || num > 2147483647) {
        notificator.notify({
          type: 'warn',
          heading: 'Invalid Batch ID',
          message: 'Batch ID must be a positive number between 1 and 2,147,483,647',
        })
        return
      }
      dispatch(updateAllClaimsRoute({ batchCode: form.batchCode, batchId: '' }))
    }

    // 2) Merge with existing grid model, but drop any fields we manage via this external form
    const existing = api.getFilterModel ? api.getFilterModel() || {} : {}
    const managed = Object.values(fields)
    const keep = Object.keys(existing).reduce((acc, k) => {
      if (!managed.includes(k)) acc[k] = (existing as any)[k]
      return acc
    }, {} as any)

    const built = buildFilterModel(form, fields, lastModQuick.model)
    const combined = { ...keep, ...built }

    // Store the metadata-enhanced filter model in Redux BEFORE setFilterModel
    // AG Grid will strip _metaData, but Redux keeps it for persistence
    dispatch(updateCommonClaimsFilters({ filters: combined }))

    setGridFilterModel(combined)
    try {
      ;(api as any).dispatchEvent?.({ type: 'filterChanged' })
      ;(api as any).onFilterChanged?.()
    } catch {
      // ignore noisy errors during grid destroy
    }
    // ClaimsTable.onFilterChanged will persist these filters from Redux
  }, [api, form, fields, lastModQuick.model, dispatch, setGridFilterModel])

  /**
   * Clear button:
   * - Reset form
   * - Reset quick last-modified to 90 days
   * - Exit batch context
   * - Keep ONLY oaiWorked (90 days) in grid filter model
   * Persistence again handled by ClaimsTable.onFilterChanged
   */
  const clearAll = useCallback(() => {
    if (!api || (api as any).isDestroyed?.()) return

    const defaultDays = 90
    const defaultDateFilter = buildRollingDateFilter(defaultDays)

    setForm(INITIAL_FILTER_FORM_STATE)
    setLastModQuick({ model: defaultDateFilter, text: `Last ${defaultDays} days` })

    // Exit batch context
    dispatch(updateAllClaimsRoute({ batchCode: '', batchId: '' }))

    const clearedModel: any = { [fields.oaiWorked]: defaultDateFilter }

    // Store metadata-enhanced filter in Redux BEFORE setFilterModel
    // Redux badge / other consumers stay in sync with metadata preserved
    dispatch(updateCommonClaimsFilters({ filters: clearedModel }))

    setGridFilterModel(clearedModel)
  }, [api, dispatch, fields.oaiWorked, setGridFilterModel])

  /**
   * ONE-TIME batch-context hydration
   *
   * When coming from batch-processing view (batchCode & batchId),
   * we:
   *  - Clear all existing filters
   *  - Apply only:
   *      - batchCode filter
   *      - Last 365 days date filter on oaiWorked
   *  - Prefill external filter form with batchCode
   *  - Push to Redux; prefs will be saved via ClaimsTable.onFilterChanged
   */
  useEffect(() => {
    if (!api || (api as any).isDestroyed?.()) return
    if (!isBatchContext || !batchCode) return
    if (batchHydratedRef.current) return
    if (!isMountedRef.current) return

    batchHydratedRef.current = true

    const batchFields = { batchCode: fields.batchCode, oaiWorked: fields.oaiWorked }
    const model = buildBatchContextFilterModel(batchCode, batchId, batchFields)
    const oaiFilter = model[fields.oaiWorked]

    // Sync local UI
    setForm({
      ...INITIAL_FILTER_FORM_STATE,
      batchCode,
    })
    setLastModQuick({ model: oaiFilter, text: 'Last 365 days' })

    // Store metadata-enhanced filters in Redux BEFORE setFilterModel
    // AG Grid will strip _metaData, but Redux keeps it for persistence
    dispatch(updateCommonClaimsFilters({ filters: model }))

    // Push to grid (prefs via ClaimsTable)
    setGridFilterModel(model)
  }, [api, isBatchContext, batchCode, batchId, fields.batchCode, fields.oaiWorked, dispatch, setGridFilterModel])

  // Skip this in batch context - batch hydration handles its own filters
  useEffect(() => {
    if (!commonClaimsFilters || isBatchContext) return
    if (!api || (api as any).isDestroyed?.()) return

    const ownerFilter = (commonClaimsFilters as any)[fields.owner]
    const statusFilter = (commonClaimsFilters as any)[fields.claimStatusPayer]
    const appealStatusFilter = (commonClaimsFilters as any)[fields.appealStatus]
    const oaiFilter = (commonClaimsFilters as any)[fields.oaiWorked]

    if (ownerFilter?.values) {
      setForm((prev) => ({ ...prev, owner: ownerFilter.values }))
    }

    if (statusFilter?.values) {
      setForm((prev) => ({ ...prev, claimStatusPayer: statusFilter.values }))
    }

    if (appealStatusFilter?.value) {
      setForm((prev) => ({ ...prev, appealStatus: normalizeAppealStatusValues(appealStatusFilter.values) }))
    }

    if (oaiFilter?.filterType === 'date' && oaiFilter.type === 'inRange' && oaiFilter._metaData?.preset) {
      // Recalculate the date range based on today's date for dynamic rollover
      const presetDays = oaiFilter._metaData.preset
      const updatedFilter = buildRollingDateFilter(presetDays)
      const displayText = `Last ${presetDays} days`

      // Only update if dates have actually changed (check if dates are stale)
      const needsUpdate =
        sanitizeYMD(oaiFilter.dateFrom) !== updatedFilter.dateFrom ||
        sanitizeYMD(oaiFilter.dateTo) !== updatedFilter.dateTo

      // Update UI state
      setLastModQuick({ model: updatedFilter, text: displayText })

      if (needsUpdate) {
        // Update the filter in Redux and grid with recalculated dates
        const updatedFilters = {
          ...commonClaimsFilters,
          [fields.oaiWorked]: updatedFilter,
        }
        dispatch(updateCommonClaimsFilters({ filters: updatedFilters }))

        // Apply to grid
        try {
          const currentModel = api.getFilterModel() || {}
          api.setFilterModel({
            ...currentModel,
            [fields.oaiWorked]: updatedFilter,
          })
        } catch {
          // Ignore if grid is being destroyed
        }
      }
    }
  }, [
    commonClaimsFilters,
    fields.owner,
    fields.claimStatusPayer,
    fields.oaiWorked,
    fields.appealStatus,
    isBatchContext,
    api,
    dispatch,
  ])

  /**
   * Keep form + lastModQuick in sync when grid filters change directly,
   * WITHOUT writing back into grid (to avoid loops).
   */
  useEffect(() => {
    if (!api || (api as any).isDestroyed?.()) return

    const syncFromGrid = () => {
      if (!isMountedRef.current || (api as any).isDestroyed?.()) return

      const m = api.getFilterModel ? api.getFilterModel() || {} : {}

      // --- Sync basic form fields ---
      setForm((prev) => {
        const extracted = extractFormFromFilterModel(m, fields, lastModQuick.model !== null)

        if (!prev || typeof prev !== 'object') {
          return extracted
        }

        return {
          ...extracted,
        }
      })

      // Use Redux filter for oaiWorked because AG Grid strips _metaData
      // Redux preserves metadata needed to distinguish presets from custom ranges
      const metadataFilters = pendingFilterModelRef.current || commonClaimsFilters
      const oaiFilter = (metadataFilters as any)?.[fields.oaiWorked] || (m as any)[fields.oaiWorked]
      if (oaiFilter?.filterType === 'date' && oaiFilter.type === 'inRange') {
        const from = sanitizeYMD(oaiFilter.dateFrom)
        const to = sanitizeYMD(oaiFilter.dateTo)
        const md = oaiFilter._metaData

        if (from && to) {
          // If preset is present, recalculate based on today's date for dynamic rollover
          if (md?.preset != null) {
            const updatedFilter = buildRollingDateFilter(md.preset)
            setLastModQuick({
              model: updatedFilter,
              text: `Last ${md.preset} days`,
            })
          } else if (md?.customRange) {
            // Explicitly marked as custom range - preserve it
            setLastModQuick({
              model: oaiFilter,
              text: `${formatDateOnly(from)} → ${formatDateOnly(to)}`,
            })
          } else {
            // No metadata - treat as custom range to preserve user selection
            const hydrated = {
              ...oaiFilter,
              _metaData: { customRange: true },
            }
            setLastModQuick({
              model: hydrated,
              text: `${formatDateOnly(from)} → ${formatDateOnly(to)}`,
            })
          }
        }
      } else if (!isBatchContext && !oaiFilter) {
        // No oaiWorked filter in normal view → default 90 days in UI
        const defaultFilter = buildRollingDateFilter(90)
        setLastModQuick({ model: defaultFilter, text: 'Last 90 days' })
      }
    }

    // Initial sync
    syncFromGrid()

    // Subscribe to AG Grid filter changes
    const listener = () => syncFromGrid()

    try {
      api.addEventListener('filterChanged', listener)
    } catch {
      // grid may be in middle of destroy; ignore
    }

    return () => {
      if (!api || (api as any).isDestroyed?.()) return
      try {
        api.removeEventListener('filterChanged', listener)
      } catch {
        // ignore noisy "grid destroyed" warnings
      }
    }
  }, [api, fields, isBatchContext])

  return {
    form,
    lastModQuick,
    filterLength,
    updateFormField,
    setLastModQuick,
    applyFilters,
    clearAll,
  }
}
